import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Calendar, Gauge, Weight, Truck, Fuel, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTruck } from '../hooks/useTrucks';
import { LoadingPage } from '../components/LoadingSpinner';
import { SoldBanner, SoldRibbon } from '../components/SoldRibbon';

const TruckDetailPage = () => {
  const { id } = useParams();
  const { truck, loading, error } = useTruck(id);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (loading) {
    return <LoadingPage message="Loading truck details..." />;
  }

  if (error || !truck) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {error || 'Truck not found'}
        </h1>
        <Link to="/trucks" className="text-orange-600 hover:text-orange-700 font-bold">
          ← Back to Trucks
        </Link>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % truck.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + truck.images.length) % truck.images.length);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/trucks" className="text-orange-500 hover:text-orange-400 font-inter inline-flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Trucks
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="relative mb-4">
              <img 
                src={truck.images[currentImageIndex]} 
                alt={truck.title}
                className={`w-full h-96 object-cover rounded-lg shadow-lg ${truck.is_sold ? 'grayscale opacity-80' : ''}`}
              />
              {truck.is_sold && <SoldRibbon size="lg" />}
              {truck.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {truck.images.length}
                  </div>
                </>
              )}
            </div>
            
            {truck.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {truck.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative rounded-lg overflow-hidden ${
                      index === currentImageIndex ? 'ring-2 ring-orange-600' : ''
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`${truck.title} ${index + 1}`}
                      className="w-full h-20 object-cover hover:opacity-75 transition-opacity"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="mb-6">
                {truck.is_sold && (
                  <div className="mb-4">
                    <SoldBanner />
                  </div>
                )}
                {truck.featured && !truck.is_sold && (
                  <span className="bg-orange-600 text-white px-3 py-1 rounded text-sm font-bold mb-3 inline-block">
                    Featured
                  </span>
                )}
                <h1 className="text-3xl font-black text-black mb-4 font-inter">{truck.title}</h1>
                <div className="text-3xl font-black mb-6">
                  {truck.is_sold ? (
                    <span className="text-red-600">SOLD</span>
                  ) : (
                    <span className="text-orange-600">{truck.price ? `£${truck.price.toLocaleString()}` : 'Price on Application'}</span>
                  )}
                </div>
              </div>

              {/* Key Specs */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="text-sm text-gray-600 font-inter">Year</div>
                    <div className="font-bold font-inter">{truck.year}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Gauge className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="text-sm text-gray-600 font-inter">Mileage</div>
                    <div className="font-bold font-inter">{truck.mileage?.toLocaleString()} km</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Weight className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="text-sm text-gray-600 font-inter">Weight</div>
                    <div className="font-bold font-inter">{truck.weight}t</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Fuel className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="text-sm text-gray-600 font-inter">Emissions</div>
                    <div className="font-bold font-inter">{truck.emissions}</div>
                  </div>
                </div>
              </div>

              {/* Contact Buttons */}
              <div className="space-y-3 mb-8">
                {truck.is_sold ? (
                  <div className="bg-gray-100 border border-gray-300 rounded p-4 text-center">
                    <p className="text-gray-700 font-bold font-inter mb-2">This vehicle has been sold</p>
                    <p className="text-sm text-gray-600 font-inter mb-3">Looking for something similar? Get in touch and we'll help you find the right vehicle.</p>
                    <Link
                      to="/trucks"
                      className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors"
                    >
                      Browse Available Stock
                    </Link>
                  </div>
                ) : (
                  <>
                    <a 
                      href="tel:01530231235"
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white px-6 py-4 rounded font-bold text-lg transition-colors flex items-center justify-center"
                    >
                      <Phone className="mr-3 h-5 w-5" />
                      Call Now: 01530 231 235
                    </a>
                    
                    <a 
                      href="mailto:info@lawmancommercials.com?subject=Enquiry about {truck.title}"
                      className="w-full bg-black hover:bg-gray-800 text-white px-6 py-4 rounded font-bold text-lg transition-colors flex items-center justify-center"
                    >
                      <Mail className="mr-3 h-5 w-5" />
                      Email Enquiry
                    </a>
                  </>
                )}
              </div>

              {/* Stock ID */}
              <div className="text-center text-sm text-gray-600 font-inter">
                Stock ID: <span className="font-bold">{truck.uniquestockid}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Specifications */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Description */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-black text-black mb-6 font-inter">Description</h2>
              <p className="text-gray-700 leading-relaxed font-inter text-lg">{truck.description}</p>
            </div>
          </div>

          {/* Specifications */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-black text-black mb-6 font-inter">Specifications</h2>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Make</span>
                  <span className="font-bold font-inter">{truck.make}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Model</span>
                  <span className="font-bold font-inter">{truck.model}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Body Type</span>
                  <span className="font-bold font-inter">{truck.bodytype}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Cab Type</span>
                  <span className="font-bold font-inter">{truck.cabtype}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Axle Configuration</span>
                  <span className="font-bold font-inter">{truck.axle}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Power</span>
                  <span className="font-bold font-inter">{truck.power}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Year</span>
                  <span className="font-bold font-inter">{truck.year}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Mileage</span>
                  <span className="font-bold font-inter">{truck.mileage?.toLocaleString()} km</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-inter">Weight</span>
                  <span className="font-bold font-inter">{truck.weight} tonnes</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 font-inter">Emissions</span>
                  <span className="font-bold font-inter">{truck.emissions}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-orange-600 text-white rounded-lg p-8">
          <div className="text-center">
            <h2 className="text-3xl font-black mb-4 font-inter">Interested in This Truck?</h2>
            <p className="text-xl mb-8 font-inter opacity-90">
              Contact our expert team for more information, viewing arrangements, or financing options.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center justify-center">
                <Phone className="mr-3 h-6 w-6" />
                <div>
                  <div className="font-bold">Office Hours</div>
                  <div>01530 231 235</div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <Phone className="mr-3 h-6 w-6" />
                <div>
                  <div className="font-bold">24/7 Emergency</div>
                  <div>07718 738 785</div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <MapPin className="mr-3 h-6 w-6" />
                <div>
                  <div className="font-bold">Visit Us</div>
                  <div>Leicester LE9 9FS</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hire Rates Section - Only show if vehicle is available for hire */}
        {truck.available_for_hire && (
          <div className="mt-12 bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-green-600 text-white p-6">
              <div className="flex items-center justify-center">
                <div className="bg-green-500/80 backdrop-blur-sm border border-green-400/30 rounded-lg px-4 py-2 mr-4">
                  <span className="text-sm font-bold">Hire Fleet</span>
                </div>
                <h2 className="text-2xl font-black font-inter">Also Available for Hire</h2>
              </div>
              <p className="text-center text-green-100 mt-2 font-inter">
                This vehicle is available for both purchase and hire
              </p>
            </div>
            
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Daily Rate */}
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <div className="text-3xl font-black text-green-600 mb-2">
                    £{truck.hire_daily_rate || 'POA'}
                  </div>
                  <div className="text-lg font-bold text-gray-700 mb-1">Daily Rate</div>
                  <div className="text-sm text-gray-500">Per day + VAT</div>
                </div>

                {/* Weekly Rate */}
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <div className="text-3xl font-black text-green-600 mb-2">
                    £{truck.hire_weekly_rate || 'POA'}
                  </div>
                  <div className="text-lg font-bold text-gray-700 mb-1">Weekly Rate</div>
                  <div className="text-sm text-gray-500">Per week + VAT</div>
                </div>

                {/* Monthly Rate */}
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <div className="text-3xl font-black text-green-600 mb-2">
                    £{truck.hire_monthly_rate || 'POA'}
                  </div>
                  <div className="text-lg font-bold text-gray-700 mb-1">Monthly Rate</div>
                  <div className="text-sm text-gray-500">Per month + VAT</div>
                </div>
              </div>

              {/* Hire Status and Booking */}
              <div className="mt-8 text-center">
                <div className="mb-4">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                    truck.hire_status === 'available' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : truck.hire_status === 'hired'
                      ? 'bg-red-100 text-red-800 border border-red-200'
                      : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                  }`}>
                    {truck.hire_status === 'available' && '✓ Available for Hire'}
                    {truck.hire_status === 'hired' && 'Currently Hired'}
                    {truck.hire_status === 'maintenance' && 'Under Maintenance'}
                  </span>
                </div>

                {truck.hire_status === 'available' && (
                  <div className="space-y-3">
                    <a 
                      href="tel:01530231235"
                      className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors mr-4"
                    >
                      <Phone className="mr-3 h-5 w-5" />
                      Call to Book Hire
                    </a>
                    <a 
                      href={`mailto:info@lawmancommercials.com?subject=Hire Enquiry: ${truck.title}&body=Hi,%0A%0AI am interested in hiring the following vehicle:%0A%0AVehicle: ${truck.title}%0AStock ID: ${truck.uniquestockid}%0A%0APlease provide me with availability and booking information.%0A%0AThanks`}
                      className="inline-flex items-center bg-gray-600 hover:bg-gray-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors"
                    >
                      <Mail className="mr-3 h-5 w-5" />
                      Email Hire Enquiry
                    </a>
                  </div>
                )}

                {truck.hire_status !== 'available' && (
                  <p className="text-gray-600 font-inter">
                    This vehicle is currently not available for hire. 
                    <a href="tel:01530231235" className="text-orange-600 hover:text-orange-700 font-bold ml-1">
                      Contact us
                    </a> for future availability.
                  </p>
                )}
              </div>

              {/* Hire Terms */}
              <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-3 font-inter">Hire Terms & Information</h3>
                <div className="text-sm text-blue-700 space-y-2 font-inter">
                  <p>• All rates are subject to VAT and availability</p>
                  <p>• Minimum hire period may apply</p>
                  <p>• Full comprehensive insurance and maintenance included</p>
                  <p>• Driver training available upon request</p>
                  <p>• Contact us for long-term hire discounts</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TruckDetailPage;